import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  DatePicker,
  Divider,
  InlineStack,
  OptionList,
  Popover,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { CalendarIcon, MenuIcon, PlusIcon } from '@shopify/polaris-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChain, faChevronRight, faFlagCheckered, faLocationDot, faPen, faTrashCan, faTruckFast, faUser } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useSettings } from 'context/SettingsContext';
import dayjs from '../../utils/dayjs';
import { useGetJourneys } from 'queries/journey.query';
import { TypedTripData, useGetTripsByJourney, useMutateChangeTimeForTrip, useMutateDeleteTrip } from 'queries/trip.query';
import HoverMarquee from 'components/HoverMarquee';
import { getTripStatusBadge } from 'entities/ticket-managers/components/tripStatusBadge';
import { ENUM_TRIP_MANIFEST_STATUS, type TypedTripManifest } from 'queries/trip_manifest.query';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import Capacity from 'components/Capacity';
import DispatchTripOrderList from './DispatchTripOrderList';
import helpers from 'helpers/index';
import { ENUM_ORDER_STATUS, TypedOrders } from 'queries/orders.query';
import { useCurrentTicketMoving } from 'context/CurrentTicketMovingContext';
import { useMutateMoving_ticket_to_trip } from 'queries/ticket.query';
import queryClient from 'queries';
import DispatchModal from './DispatchModal';
import 'media/css/dispatch.scss';

type MapboxGlobals = {
  accessToken: string;
  Map: new (...args: any[]) => any;
  Marker: new (...args: any[]) => any;
  Popup: new (...args: any[]) => any;
  LngLatBounds: new (...args: any[]) => any;
};
declare global {
  interface Window {
    mapboxgl?: MapboxGlobals;
  }
}
const MAPBOX_SCRIPT_ID = 'mapbox-gl-js';
const MAPBOX_STYLE_ID = 'mapbox-gl-css';
const DEFAULT_CENTER: [number, number] = [105.8342, 21.0278];
const DispatchTripDetailPanel = lazy(() => import('./DispatchTripDetailPanel'));
const TripTimeModal = lazy(() => import('entities/ticket-managers/tripTimeModal'));

const getDispatchQuery = () => {
  const params = new URLSearchParams(window.location.search);
  const queryDate = params.get('d') || '';
  return {
    date: /^\d{4}-\d{2}-\d{2}$/.test(queryDate) ? queryDate : dayjs().format('YYYY-MM-DD'),
    journeyId: params.get('journey_id') || '',
    tripId: params.get('h') || '',
  };
};

const loadMapboxAssets = () => {
  if (!document.getElementById(MAPBOX_STYLE_ID)) {
    const link = document.createElement('link');
    link.id = MAPBOX_STYLE_ID;
    link.rel = 'stylesheet';
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css';
    document.head.appendChild(link);
  }
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  return new Promise<MapboxGlobals>((resolve, reject) => {
    const existing = document.getElementById(MAPBOX_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(window.mapboxgl as MapboxGlobals));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = MAPBOX_SCRIPT_ID;
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => resolve(window.mapboxgl as MapboxGlobals);
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

export default function Dispatch() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const mapToken = settings?._mapbox_public_key || '';
  const initialQuery = useMemo(getDispatchQuery, []);
  const [selectedDate, setSelectedDate] = useState(initialQuery.date);
  const [selectedJourneyId, setSelectedJourneyId] = useState(initialQuery.journeyId);
  const [selectedTripId, setSelectedTripId] = useState(initialQuery.tripId);
  const [panelOpen, setPanelOpen] = useState(true);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [editingTripId, setEditingTripId] = useState('');
  const [editingTime, setEditingTime] = useState('');
  const [tripTimeModalOpen, setTripTimeModalOpen] = useState(false);
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [detailSelection, setDetailSelection] = useState<{ tripId: string; tripManifestId: string; tripTime: string } | null>(null);
  const closeTripDetail = useCallback(() => setDetailSelection(null), []);
  const initialCalendarDate = new Date(`${initialQuery.date}T00:00:00`);
  const [{ month, year }, setCalendar] = useState({ month: initialCalendarDate.getMonth(), year: initialCalendarDate.getFullYear() });
  const [mapReady, setMapReady] = useState(false);
  const [loadingMapPoints, setLoadingMapPoints] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const mapMarkersRef = useRef<any[]>([]);
  const { showDeleteConfirm } = useDeleteConfirm();
  const { addNotification } = useNotification();
  const { mutateAsync: changeTripTime, isPending: changingTripTime } = useMutateChangeTimeForTrip();
  const { mutateAsync: deleteTrip, isPending: deletingTrip } = useMutateDeleteTrip();
  const { currentTicketMoving, setCurrentTicketMoving, clearCurrentTicketMoving } = useCurrentTicketMoving();
  const { mutateAsync: moveTicket, isPending: movingTickets } = useMutateMoving_ticket_to_trip();

  const { data: journeyResponse, isLoading: loadingJourneys } = useGetJourneys({ limit: 100, sort: 'createdAt:asc' });
  const journeys = useMemo(() => journeyResponse?.body || [], [journeyResponse]);

  useEffect(() => {
    if (!selectedJourneyId && journeys.length > 0) {
      setSelectedJourneyId(String(journeys[0].id));
    }
  }, [journeys, selectedJourneyId]);

  // useGetTripsByJourney chuẩn hóa bằng setUTCHours, nên phải truyền ngày ở UTC
  // giống ticket_managers. Thêm T00:00:00 sẽ khiến múi giờ Việt Nam bị lùi 1 ngày.
  const tripDate = useMemo(() => new Date(selectedDate), [selectedDate]);
  const { data: tripResponse, isLoading: loadingTrips } = useGetTripsByJourney(selectedJourneyId, tripDate);
  const trips = useMemo(() => tripResponse?.data || [], [tripResponse]);
  const isToday = useMemo(() => selectedDate === dayjs().format('YYYY-MM-DD'), [selectedDate]);
  const { upcomingTrips, pastTrips } = useMemo(() => {
    const sortedTrips = [...trips].sort((tripA, tripB) => (tripA.time || '').localeCompare(tripB.time || ''));
    if (!isToday) return { upcomingTrips: sortedTrips, pastTrips: [] as TypedTripData[] };

    const now = new Date();
    return sortedTrips.reduce(
      (groups, trip) => {
        const [hours, minutes] = (trip.time || '00:00').split(':').map((value) => Number(value) || 0);
        const departureTime = new Date(`${selectedDate}T00:00:00`);
        departureTime.setHours(hours, minutes, 0, 0);
        groups[departureTime < now ? 'pastTrips' : 'upcomingTrips'].push(trip);
        return groups;
      },
      { upcomingTrips: [] as TypedTripData[], pastTrips: [] as TypedTripData[] }
    );
  }, [isToday, selectedDate, trips]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('d', selectedDate);
    if (selectedJourneyId) params.set('journey_id', selectedJourneyId);
    if (selectedTripId) params.set('h', selectedTripId);
    const nextUrl = `/dispatch?${params.toString()}`;
    if (`${window.location.pathname}${window.location.search}` !== nextUrl) window.history.replaceState(null, 'dispatch', nextUrl);
  }, [selectedDate, selectedJourneyId, selectedTripId]);

  useEffect(() => {
    setDetailSelection(null);
  }, [selectedTripId]);

  useEffect(() => {
    const restoreQueryFromHistory = () => {
      const query = getDispatchQuery();
      setSelectedDate(query.date);
      setSelectedJourneyId(query.journeyId);
      setSelectedTripId(query.tripId);
    };
    window.addEventListener('popstate', restoreQueryFromHistory);
    return () => window.removeEventListener('popstate', restoreQueryFromHistory);
  }, []);

  useEffect(() => {
    let mounted = true;
    loadMapboxAssets()
      .then(() => mounted && setMapReady(true))
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    if (!mapReady || !mapToken || !mapContainerRef.current || mapRef.current || !window.mapboxgl) return;
    window.mapboxgl.accessToken = mapToken;
    const map = new window.mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: DEFAULT_CENTER,
      zoom: 5.5,
    });
    mapRef.current = map;
    return () => {
      mapMarkersRef.current.forEach((marker) => marker.remove());
      mapMarkersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [mapReady, mapToken]);

  const clearMapMarkers = useCallback(() => {
    mapMarkersRef.current.forEach((marker) => marker.remove());
    mapMarkersRef.current = [];
  }, []);

  const focusMapPoint = useCallback(
    (placeId: string, pointLabel: string) => {
      const coordinates = String(placeId)
        .split(',')
        .map((value) => Number(value.trim()));
      if (coordinates.length !== 2 || !coordinates.every(Number.isFinite)) {
        addNotification('error', `Tọa độ ${pointLabel} không hợp lệ.`);
        return;
      }
      const [latitude, longitude] = coordinates;
      if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        addNotification('error', `Tọa độ ${pointLabel} không hợp lệ.`);
        return;
      }
      mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 16, duration: 800, essential: true });
    },
    [addNotification]
  );

  const showMapPoints = async (trip: TypedTripData, pointType: 'pickup' | 'dropoff') => {
    const isPickup = pointType === 'pickup';
    const pointLabel = isPickup ? 'điểm đón' : 'điểm trả';
    const tripManifestId = String(trip.trip_manifest?.id || '');
    if (!tripManifestId || !mapRef.current || !window.mapboxgl) {
      addNotification('error', 'Bản đồ hoặc lệnh điều xe chưa sẵn sàng.');
      return;
    }

    setLoadingMapPoints(true);
    try {
      const endpoint = helpers.buildEndUrl({ trip_manifest_id: tripManifestId, limit: 400, sort: 'createdAt:asc' });
      const { data: orders } = await axios.get<TypedOrders[]>(`/orders/${endpoint}`);
      const mapPoints = orders.flatMap((order) => {
        if (order.order_status === ENUM_ORDER_STATUS.CANCELLED) return [];
        const coordinates = String((isPickup ? order.pickup_place_id : order.dropoff_place_id) || '')
          .split(',')
          .map((value) => Number(value.trim()));
        if (coordinates.length !== 2 || !coordinates.every(Number.isFinite)) return [];
        const [latitude, longitude] = coordinates;
        if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return [];
        return [{ order, latitude, longitude }];
      });

      clearMapMarkers();
      if (!mapPoints.length) {
        addNotification('error', `Chuyến này chưa có ${pointLabel} hợp lệ.`);
        return;
      }

      const bounds = {
        minLng: Infinity,
        minLat: Infinity,
        maxLng: -Infinity,
        maxLat: -Infinity,
      };

      mapPoints.forEach(({ order, latitude, longitude }) => {
        const customerName =
          order.customer_data?.display_name ||
          order.customer_name ||
          (isPickup ? order.pickup_contact_name : order.dropoff_contact_name) ||
          'Khách hàng';
        const customerPhone =
          order.customer_data?.user_phonenumber ||
          order.customer_phone ||
          (isPickup ? order.pickup_contact_phonenumber : order.dropoff_contact_phonenumber) ||
          'Chưa có số điện thoại';
        const pointAddress = (isPickup ? order.pickup_address : order.dropoff_address) || `Chưa có địa chỉ ${isPickup ? 'đón' : 'trả'}`;
        const avatar = order.customer_data?.user_avatar ? helpers.getMediaLink(order.customer_data.user_avatar) : '';
        const initials = customerName
          .split(/\s+/)
          .filter(Boolean)
          .slice(-2)
          .map((part) => part[0])
          .join('')
          .toUpperCase();

        const markerElement = document.createElement('button');
        markerElement.type = 'button';
        markerElement.className = `dispatch-customer-marker dispatch-customer-marker--${pointType}`;
        markerElement.title = `${customerName} - ${pointAddress}`;
        markerElement.setAttribute('aria-label', markerElement.title);
        const markerContent = document.createElement('span');
        markerContent.className = 'dispatch-customer-marker__content';
        if (avatar) {
          const image = document.createElement('img');
          image.src = avatar;
          image.alt = '';
          image.addEventListener('error', () => {
            image.remove();
            markerContent.textContent = initials || '?';
          });
          markerContent.appendChild(image);
        } else {
          markerContent.textContent = initials || '?';
        }
        markerElement.appendChild(markerContent);

        const popupContent = document.createElement('div');
        popupContent.className = 'dispatch-customer-popup';
        const popupName = document.createElement('strong');
        popupName.textContent = customerName;
        const popupPhone = document.createElement('a');
        popupPhone.href = `tel:${customerPhone}`;
        popupPhone.textContent = customerPhone;
        const popupAddress = document.createElement('button');
        popupAddress.type = 'button';
        popupAddress.className = 'dispatch-customer-popup__address';
        popupAddress.textContent = pointAddress;
        popupAddress.title = `Đi tới ${pointLabel} này`;
        popupAddress.addEventListener('click', () => {
          mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 16, duration: 800, essential: true });
          popup.remove();
        });
        popupContent.append(popupName, popupPhone, popupAddress);

        const popup = new window.mapboxgl.Popup({ offset: 22, closeButton: true }).setDOMContent(popupContent);
        const marker = new window.mapboxgl.Marker({ element: markerElement, anchor: 'bottom' })
          .setLngLat([longitude, latitude])
          .setPopup(popup)
          .addTo(mapRef.current);
        mapMarkersRef.current.push(marker);
        bounds.minLng = Math.min(bounds.minLng, longitude);
        bounds.minLat = Math.min(bounds.minLat, latitude);
        bounds.maxLng = Math.max(bounds.maxLng, longitude);
        bounds.maxLat = Math.max(bounds.maxLat, latitude);
      });

      mapRef.current.fitBounds(
        [
          [bounds.minLng, bounds.minLat],
          [bounds.maxLng, bounds.maxLat],
        ],
        { padding: { top: 70, right: 70, bottom: 70, left: panelOpen ? 430 : 70 }, maxZoom: 15, duration: 700 }
      );
    } catch (error: any) {
      addNotification('error', error?.message || `Không thể tải các ${pointLabel}.`);
    } finally {
      setLoadingMapPoints(false);
    }
  };

  const selectJourney = (journeyId: string) => {
    setSelectedJourneyId(journeyId);
    setSelectedTripId('');
    setDetailSelection(null);
  };
  const selectDate = (date: Date) => {
    setSelectedDate(dayjs(date).format('YYYY-MM-DD'));
    setSelectedTripId('');
    setDetailSelection(null);
  };
  const dateAtOffset = (offset: number) => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return date;
  };
  const saveTripTime = async (trip: TypedTripData) => {
    if (!trip.id || !editingTime || !/^([01]\d|2[0-3]):[0-5]\d$/.test(editingTime)) {
      addNotification('error', 'Giờ chạy phải có định dạng HH:mm.');
      return;
    }
    try {
      const result = await changeTripTime({ trip_id: String(trip.id), time_string: editingTime });
      setSelectedTripId(String(result?.id || trip.id));
      setEditingTripId('');
      addNotification('info', 'Đổi giờ chạy thành công.');
    } catch (error: any) {
      addNotification('error', error?.message || 'Không thể đổi giờ chạy.');
    }
  };
  const deleteTripById = async (trip: TypedTripData) => {
    if (!trip.id) return;
    const confirmed = await showDeleteConfirm(
      'Xóa giờ chạy này?',
      'Bạn cần xóa hết vé và chuyến trước khi xóa giờ chạy. Hành động này không thể khôi phục.'
    );
    if (!confirmed) return;
    try {
      await deleteTrip(trip.id);
      if (selectedTripId === String(trip.id)) setSelectedTripId('');
      addNotification('info', 'Xóa giờ chạy thành công.');
    } catch (error: any) {
      addNotification('error', error?.message || 'Không thể xóa giờ chạy.');
    }
  };
  const toggleTripDetail = (trip: TypedTripData) => {
    const tripId = String(trip.id || '');
    if (detailSelection?.tripId === tripId) {
      setDetailSelection(null);
      return;
    }
    setDetailSelection({
      tripId,
      tripManifestId: String(trip.trip_manifest?.id || ''),
      tripTime: trip.time || '--:--',
    });
  };

  const moveTicketsToTrip = async (trip: TypedTripData, seatPosition: string) => {
    const tripManifestId = String(trip.trip_manifest?.id || '');

    if (!currentTicketMoving.length || movingTickets || !seatPosition) return;
    if (!tripManifestId) {
      addNotification('error', 'Chuyến đích chưa có lệnh điều xe.');
      return;
    }

    try {
      await Promise.all(
        currentTicketMoving.map((ticketId) =>
          moveTicket({
            trip_manifest_id: tripManifestId,
            ticket_id: ticketId,
            seat_position: seatPosition,
          })
        )
      );
      const movedCount = currentTicketMoving.length;
      clearCurrentTicketMoving();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] }),
        queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] }),
      ]);
      addNotification('info', `Đã chuyển ${movedCount} vé sang chuyến ${trip.time || ''}.`);
    } catch (error: any) {
      addNotification('error', error?.message || 'Không thể chuyển vé sang chuyến khác.');
    }
  };

  const renderTrip = (trip: TypedTripData) => {
    const manifest = trip.trip_manifest;
    const totalSeats = manifest?.seating?.seating_available_count || 0;
    const occupiedSeats = manifest?.total_occupied_seats || 0;
    const occupiedGoods = manifest?.total_occupied_good || 0;
    const isOverbooked = totalSeats > 0 && occupiedSeats > totalSeats;
    const occupancyPercentage = totalSeats > 0 ? Math.max(0, Math.min(100, Math.floor((occupiedSeats / totalSeats) * 100))) : 0;
    const seatingName = manifest?.seating?.seating_name || 'Chưa có loại ghế';
    const drivers = [...(manifest?.trip_manifest_driver || [])].sort((driverA, driverB) => {
      const roleScore = (role?: string) => (role === 'driver' ? 0 : 1);
      return roleScore(driverA.driver_role) - roleScore(driverB.driver_role);
    });
    const mainDrivers = drivers.filter((driver) => driver.driver_role === 'driver');
    const assistantDrivers = drivers.filter((driver) => driver.driver_role !== 'driver');
    const active = selectedTripId === String(trip.id);
    return (
      <div
        key={trip.id}
        className={`dispatch-trip ${active ? 'dispatch-trip--active' : ''} ${detailSelection?.tripId === String(trip.id) ? 'dispatch-trip--detail-open' : ''}`}
      >
        <button
          className="dispatch-trip__summary"
          onClick={() => setSelectedTripId(active ? '' : String(trip.id || ''))}
          aria-expanded={active}
          aria-controls={`dispatch-trip-actions-${trip.id}`}
        >
          <div className="dispatch-trip__time">{trip.time || '--:--'}</div>
          <div className="dispatch-trip__body">
            <InlineStack align="space-between" blockAlign="center" gap="200" wrap={false}>
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                {manifest && manifest.trip_status > ENUM_TRIP_MANIFEST_STATUS.DISPATCHED ? getTripStatusBadge(manifest.trip_status) : null}
                <Text as="span" fontWeight="semibold">
                  {manifest?.vehicle?.license_plate || 'Chưa điều xe'}
                </Text>
              </InlineStack>
              <div className="dispatch-trip__ticket-types">
                <Tooltip content={`${occupiedSeats} hành khách`}>
                  <span>
                    <FontAwesomeIcon icon={faUser} /> {occupiedSeats}
                  </span>
                </Tooltip>
                {occupiedGoods > 0 ? (
                  <Tooltip content={`${occupiedGoods} vé hàng`}>
                    <span className="dispatch-trip__ticket-type--goods">
                      <FontAwesomeIcon icon={faTruckFast} /> {occupiedGoods}
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            </InlineStack>
            <Text as="p" variant="bodySm">
              {seatingName}
            </Text>
            <div className="dispatch-trip__occupancy">
              <div className="dispatch-trip__occupancy-track">
                <div
                  className={`dispatch-trip__occupancy-fill ${isOverbooked ? 'dispatch-trip__occupancy-fill--overbooked' : occupancyPercentage >= 90 ? 'dispatch-trip__occupancy-fill--full' : ''}`}
                  style={{ width: `${occupancyPercentage}%` }}
                />
              </div>
            </div>
            <div className="dispatch-trip__drivers">
              {drivers.length ? (
                <div className="dispatch-trip__driver-line">
                  {mainDrivers.map((driver, index) => (
                    <span className="dispatch-trip__driver--main" key={`${driver.driver_data?.display_name || 'main-driver'}-${index}`}>
                      {index > 0 ? ', ' : ''}
                      {driver.driver_data?.display_name || 'Chưa có tên'}
                    </span>
                  ))}
                  {assistantDrivers.length ? (
                    <HoverMarquee className="dispatch-trip__assistant-marquee" speed={80}>
                      {assistantDrivers.map((driver, index) => (
                        <span className="dispatch-trip__driver--assistant" key={`${driver.driver_data?.display_name || 'assistant-driver'}-${index}`}>
                          {mainDrivers.length > 0 || index > 0 ? ', ' : ''}
                          {driver.driver_data?.display_name || 'Chưa có tên'}
                        </span>
                      ))}
                    </HoverMarquee>
                  ) : null}
                </div>
              ) : (
                <Text as="p" variant="bodySm" tone="subdued">
                  Chưa phân công tài xế
                </Text>
              )}
            </div>
          </div>
        </button>
        {active && (
          <div className="dispatch-trip__expanded" id={`dispatch-trip-actions-${trip.id}`}>
            <div className="dispatch-trip__actions">
              <div className="dispatch-trip__action-group">
                <Popover
                  active={editingTripId === String(trip.id)}
                  activator={
                    <button
                      type="button"
                      className="dispatch-trip__action-button"
                      onClick={() => {
                        setEditingTripId(String(trip.id || ''));
                        setEditingTime(trip.time || '');
                      }}
                      aria-label="Đổi giờ"
                      title="Đổi giờ"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  }
                  onClose={() => setEditingTripId('')}
                  autofocusTarget="first-node"
                >
                  <Popover.Pane>
                    <Box padding="400" width="240px">
                      <BlockStack gap="300">
                        <TextField
                          label="Giờ chạy mới"
                          value={editingTime}
                          onChange={setEditingTime}
                          placeholder="HH:mm"
                          autoComplete="off"
                          maxLength={5}
                        />
                        <Button variant="primary" size="micro" loading={changingTripTime} onClick={() => saveTripTime(trip)}>
                          Lưu
                        </Button>
                      </BlockStack>
                    </Box>
                  </Popover.Pane>
                </Popover>
                <button
                  type="button"
                  className="dispatch-trip__action-button dispatch-trip__action-button--delete"
                  disabled={deletingTrip}
                  onClick={() => deleteTripById(trip)}
                  aria-label="Xóa chuyến"
                  title="Xóa chuyến"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
                <button
                  type="button"
                  className="dispatch-trip__action-button"
                  onClick={() => {
                    const params = new URLSearchParams({
                      d: selectedDate,
                      journey_id: selectedJourneyId,
                      h: String(trip.id),
                    });
                    navigate(`/ticket_managers?${params.toString()}`);
                  }}
                  aria-label="Mở màn hình điều hành"
                  title="Mở màn hình điều hành"
                >
                  <FontAwesomeIcon icon={faChain} />
                </button>
                <button
                  type="button"
                  className="dispatch-trip__action-button dispatch-trip__action-button--pickup-map"
                  disabled={loadingMapPoints}
                  onClick={() => showMapPoints(trip, 'pickup')}
                  aria-label="Xem map điểm đón"
                  title="Xem map điểm đón"
                >
                  {loadingMapPoints ? <Spinner accessibilityLabel="Đang tải điểm đón" size="small" /> : <FontAwesomeIcon icon={faLocationDot} />}
                </button>
                <button
                  type="button"
                  className="dispatch-trip__action-button dispatch-trip__action-button--dropoff-map"
                  disabled={loadingMapPoints}
                  onClick={() => showMapPoints(trip, 'dropoff')}
                  aria-label="Xem map điểm trả"
                  title="Xem map điểm trả"
                >
                  {loadingMapPoints ? <Spinner accessibilityLabel="Đang tải điểm trả" size="small" /> : <FontAwesomeIcon icon={faFlagCheckered} />}
                </button>
              </div>
              <button
                type="button"
                className="dispatch-trip__detail-icon"
                onClick={() => toggleTripDetail(trip)}
                aria-expanded={detailSelection?.tripId === String(trip.id)}
                aria-controls="dispatch-trip-detail-panel"
                aria-label="Mở thông tin chi tiết chuyến"
                title="Mở thông tin chi tiết chuyến"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
            <Divider />
            <DispatchTripOrderList
              tripData={trip}
              seating={manifest?.seating}
              tripManifestId={String(manifest?.id || '')}
              movingTicketIds={currentTicketMoving}
              movingTickets={movingTickets}
              onMoveToSeat={manifest?.trip_status < ENUM_TRIP_MANIFEST_STATUS.COMPLETED ? (position) => moveTicketsToTrip(trip, position) : undefined}
              onSelectTicketsToMove={setCurrentTicketMoving}
              onPickupAddressClick={(placeId) => focusMapPoint(placeId, 'điểm đón')}
              onDropoffAddressClick={(placeId) => focusMapPoint(placeId, 'điểm trả')}
            />
            {/* 
            {manifest?.trip_status < ENUM_TRIP_MANIFEST_STATUS.COMPLETED && (
              <>
                <br />
                <br />
                {currentTicketMoving.length === 0 && (
                  <Button icon={PlusIcon} tone="success" variant="secondary" onClick={() => {}}>
                    Lên đơn
                  </Button>
                )}
              </>
            )} */}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Điều xe</title>
      </Helmet>
      <div id="dispatch-container">
        <div ref={mapContainerRef} className="dispatch-map" />
        {!mapToken && <div className="dispatch-map-notice">Chưa cấu hình Mapbox Access Token</div>}
        <aside className={`dispatch-panel ${panelOpen ? '' : 'dispatch-panel--collapsed'}`}>
          <div className="dispatch-panel__toolbar">
            <Button
              variant="monochromePlain"
              icon={MenuIcon}
              onClick={() => setPanelOpen((open) => !open)}
              accessibilityLabel="Đóng mở bảng điều xe"
            />
            <Text as="h1" variant="headingLg">
              Điều xe
            </Text>
            <div className="dispatch-panel__ai-action">
              <Button variant="monochromePlain" size="slim" onClick={() => setDispatchModalOpen(true)}>
                Điều tài
              </Button>
            </div>
          </div>
          {panelOpen && (
            <div className="dispatch-panel__content">
              <div className="dispatch-panel__scroll">
                {loadingJourneys && (
                  <Box padding="400">
                    <InlineStack align="center">
                      <Spinner size="small" />
                    </InlineStack>
                  </Box>
                )}
                {!loadingJourneys && (
                  <div className="dispatch-journey-options">
                    <OptionList
                      title=""
                      onChange={(values) => selectJourney(values[0])}
                      options={journeys.map((journey) => ({ value: String(journey.id), label: journey.journey_name || 'Tuyến chưa đặt tên' }))}
                      selected={selectedJourneyId ? [selectedJourneyId] : []}
                    />
                  </div>
                )}
                <Divider />
                <div className="dispatch-date-controls">
                  <ButtonGroup variant="segmented" fullWidth>
                    {[
                      { label: 'Hôm nay', offset: 0 },
                      { label: 'Ngày mai', offset: 1 },
                      { label: 'Ngày kia', offset: 2 },
                    ].map(({ label, offset }) => {
                      const date = dateAtOffset(offset);
                      const active = selectedDate === dayjs(date).format('YYYY-MM-DD');
                      return (
                        <Button key={label} onClick={() => selectDate(date)} variant={active ? 'primary' : 'secondary'}>
                          {label}
                        </Button>
                      );
                    })}
                  </ButtonGroup>
                  <Popover
                    active={datePopoverOpen}
                    activator={
                      <Button variant="secondary" icon={CalendarIcon} fullWidth onClick={() => setDatePopoverOpen((open) => !open)}>
                        {dayjs(selectedDate).format('DD-MM-YYYY')}
                      </Button>
                    }
                    autofocusTarget="first-node"
                    onClose={() => setDatePopoverOpen(false)}
                  >
                    <Popover.Pane>
                      <Popover.Section>
                        <DatePicker
                          month={month}
                          year={year}
                          onMonthChange={(nextMonth, nextYear) => setCalendar({ month: nextMonth, year: nextYear })}
                          selected={{ start: new Date(`${selectedDate}T00:00:00`), end: new Date(`${selectedDate}T00:00:00`) }}
                          onChange={({ start }) => {
                            selectDate(start);
                            setDatePopoverOpen(false);
                          }}
                        />
                      </Popover.Section>
                    </Popover.Pane>
                  </Popover>
                </div>
                <Divider />
                <div className="dispatch-trip-list">
                  {!selectedJourneyId && (
                    <Text as="p" tone="subdued">
                      Chọn tuyến đường trước, sau đó các chuyến sẽ xuất hiện tại đây.
                    </Text>
                  )}
                  {selectedJourneyId && loadingTrips && (
                    <Box padding="300">
                      <InlineStack align="center">
                        <Spinner size="small" />
                      </InlineStack>
                    </Box>
                  )}
                  {selectedJourneyId && !loadingTrips && !trips.length && (
                    <Text as="p" tone="subdued">
                      Không có chuyến trong ngày này.
                    </Text>
                  )}
                  {selectedJourneyId &&
                    !loadingTrips &&
                    trips.length > 0 &&
                    (isToday ? (
                      <BlockStack gap="400">
                        <BlockStack gap="200">
                          <Text as="h3" variant="headingMd" tone="subdued">
                            Chưa tới giờ khởi hành
                          </Text>
                          {upcomingTrips.length > 0 ? (
                            upcomingTrips.map(renderTrip)
                          ) : (
                            <Text as="p" tone="subdued">
                              Không còn chuyến nào chưa khởi hành.
                            </Text>
                          )}
                        </BlockStack>

                        <Divider />

                        <BlockStack gap="200">
                          <Text as="h3" variant="headingMd" tone="subdued">
                            Đã khởi hành
                          </Text>
                          {pastTrips.length > 0 ? (
                            pastTrips.map(renderTrip)
                          ) : (
                            <Text as="p" tone="subdued">
                              Chưa có chuyến nào đã khởi hành.
                            </Text>
                          )}
                        </BlockStack>
                      </BlockStack>
                    ) : (
                      upcomingTrips.map(renderTrip)
                    ))}
                  {selectedJourneyId && !loadingTrips && (
                    <Capacity current_user_can="manage_trip_manifest">
                      <div className="dispatch-trip-add">
                        <br />
                        <Button variant="primary" size="large" icon={PlusIcon} fullWidth onClick={() => setTripTimeModalOpen(true)}>
                          Thêm giờ chạy
                        </Button>
                        <br />
                        <br />
                      </div>
                    </Capacity>
                  )}
                </div>
              </div>
            </div>
          )}
        </aside>
        {panelOpen && detailSelection && (
          <Suspense fallback={null}>
            <DispatchTripDetailPanel tripManifestId={detailSelection.tripManifestId} onClose={closeTripDetail} />
          </Suspense>
        )}
        {tripTimeModalOpen && (
          <Suspense fallback={null}>
            <TripTimeModal
              show
              current_journey_id={selectedJourneyId}
              current_departure_date={new Date(selectedDate)}
              onClose={(createdTrip) => {
                setTripTimeModalOpen(false);
                if (createdTrip?.id) setSelectedTripId(String(createdTrip.id));
              }}
            />
          </Suspense>
        )}
        <DispatchModal
          open={dispatchModalOpen}
          departureDate={selectedDate}
          activeJourneyId={selectedJourneyId}
          onClose={() => setDispatchModalOpen(false)}
        />
      </div>
    </>
  );
}
